<template>
  <div class="account-content-card">
    <div class="card-content">
      <section class="section-wrapper" aria-labelledby="personal-info-heading">
        <h3 id="personal-info-heading" class="section-header">
          Personal Information
        </h3>

        <div
          v-if="isEditPersonalInformation"
          class="edit-personal-info-container"
        >
          <div class="name-inputs">
            <BaseInput
              class="personal-info-input"
              v-model="firstName"
              label="First Name"
            />
            <BaseInput
              class="personal-info-input"
              v-model="lastName"
              label="Last Name"
            />
          </div>
          <BaseInput
            v-model="email"
            label="Email"
            disabled
            class="email-input"
          />
        </div>
        <div v-else class="info-display-container">
          <div class="info-field">
            <p class="info-label">Name</p>
            <p class="info-value">
              {{ user?.fullname }}
            </p>
          </div>

          <div class="info-field">
            <p class="info-label">Email</p>
            <p class="info-value">
              {{ user?.email }}
            </p>
          </div>
        </div>
      </section>

      <div class="divider"></div>

      <SchoolGradeSubjectSection
        ref="schoolSectionRef"
        :user="user"
        :isEditing="isEditSchool"
      />

      <div class="divider"></div>

      <div class="edit-account-button-wrapper">
        <ActionButton
          v-if="!isEditPersonalInformation && !isEditSchool"
          @click="startEdit"
          variant="secondary"
        >
          Edit Account
        </ActionButton>
        <div v-else class="edit-actions">
          <ActionButton variant="secondary" @click="cancelEdit"
            >Cancel</ActionButton
          >
          <ActionButton
            @click="savePersonalInfo"
            variant="primary"
            :loading="isLoading"
            class="save-account-button"
          >
            Save
          </ActionButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import cookiesService from "@/services/cookiesService";
import ysaAdminService from "@/services/ysaAdminService";
import moment from "moment-timezone";
import { inject, onMounted, ref } from "vue";
import BaseInput from "../BaseInput.vue";
import userService from "@/services/userService";
import ActionButton from "../common/ActionButton.vue";
import SchoolGradeSubjectSection from "./SchoolGradeSubjectSection.vue";

const isEditPersonalInformation = ref(false);
const isEditSchool = ref(false);
const timeZones = ref([]);
const user = ref(null);
const isLoading = ref(true);
const firstName = ref("");
const lastName = ref("");
const email = ref("");
const schoolSectionRef = ref(null);

const filter = ref({
  limit: 10,
});

const schoolList = ref([]);

const notify = inject("notify");

const checkAuthToken = async () => {
  isLoading.value = true;
  try {
    const userData = await cookiesService.getCookie("authToken");
    if (userData) {
      user.value = userData;

      if (user.value?.timezone) {
        timeZone.value = user.value.timezone;
      }

      window.dispatchEvent(new Event("userDataUpdated"));
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const savePersonalInfo = async () => {
  try {
    isLoading.value = true;

    const updateData = {
      firstname: firstName.value,
      lastname: lastName.value,
    };

    if (isEditSchool.value && schoolSectionRef.value) {
      const schoolData = schoolSectionRef.value.getSaveData();
      Object.assign(updateData, schoolData);
    }

    await userService.updateProfile(updateData);

    const response = await userService.getProfile();
    const data = response.data?.data;
    if (data) {
      user.value = data;
    }
    isEditPersonalInformation.value = false;
    isEditSchool.value = false;
    notify.success("Profile has been changed");
  } catch (error) {
    console.error("Error saving account information:", error);
  } finally {
    isLoading.value = false;
  }
};

const startEdit = () => {
  if (user.value) {
    firstName.value = user.value.firstname;
    lastName.value = user.value.lastname;
    email.value = user.value.email;
    isEditPersonalInformation.value = true;
    isEditSchool.value = true;

    // Initialize child component edit state
    if (schoolSectionRef.value) {
      schoolSectionRef.value.initEdit();
    }
  }
};

const cancelEdit = () => {
  isEditPersonalInformation.value = false;
  isEditSchool.value = false;
};

const getAllSchool = async () => {
  try {
    const response = await ysaAdminService.getAllSchool(filter.value);

    if (response.status === 200) {
      console.log(response.data);
      const schools = response?.data?.data?.rows || [];

      schoolList.value = schools.map((school) => ({
        value: school.id,
        text: school.title,
      }));
    }
  } catch (error) {
    console.log(error);
  }
};

const timeZone = ref("");

const loadTimeZones = () => {
  const now = new Date();

  timeZones.value = moment.tz.names().map((zone) => {
    const currentTime = moment.tz(now, zone);
    const offset = currentTime.format("Z");
    const displayName = `${currentTime.format("HH:mm")}, ${zone.replace(
      "_",
      " "
    )} (GMT${offset})`;

    return {
      value: zone,
      text: displayName,
    };
  });
};

onMounted(() => {
  checkAuthToken();
  getAllSchool();
  loadTimeZones();
});
</script>

<style scoped>
.card-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.section-header {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-xxl);
  line-height: 100%;
  color: #e00069;
  margin: 0;
}

.divider {
  height: 1px;
  background-color: #d8dae5;
  width: 100%;
  margin: 0;
}

.info-display-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-field {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.info-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--neutral-700);
  opacity: 0.5;
  margin: 0;
}

.info-value {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
  color: #3b4648;
  margin: 0;
}

.edit-personal-info-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.name-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.personal-info-input,
.email-input {
  font-size: var(--fs-large);
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  color: #3b4648;
}

.full-width {
  width: 100%;
}

.edit-account-button-wrapper {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.edit-account-button {
  width: 150px;
  height: 56px;
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  background-color: transparent;
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.edit-account-button:hover {
  background-color: rgba(0, 61, 106, 0.1);
}

.edit-actions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.save-account-button {
  min-width: 120px;
}

.cancel-button {
  padding: 10px 20px;
  border: none;
  background-color: transparent;
  color: #4c4ddc;
  border-bottom: 1px solid #4c4ddc;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-button:hover {
  color: #3a3bb8;
  border-bottom-color: #3a3bb8;
}

/* Mobile Styles */
@media (max-width: 768px) {
  .section-header {
    font-size: var(--fs-large);
    line-height: 28px;
    font-weight: 500;
  }

  .info-label {
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
  }

  .info-value {
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
  }

  /* Edit Mode - Personal Information */
  .edit-personal-info-container {
    gap: 16px;
  }

  .name-inputs > * {
    flex: 1;
  }

  .personal-info-input :deep(.input-label),
  .email-input :deep(.input-label) {
    font-family: "League Spartan", sans-serif;
    font-weight: 400;
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    color: #3b4648;
    margin-bottom: 4px;
    width: 100%;
  }

  .personal-info-input :deep(.input-main),
  .email-input :deep(.input-main) {
    height: 40px;
    width: 100%;
    padding: 4px 16px;
    border-radius: 8px;
    border: 1px solid #6c737f;
    font-family: "League Spartan", sans-serif;
    font-weight: 500;
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    color: #3b4648;
    box-sizing: border-box;
  }

  .email-input :deep(.input-main) {
    background-color: #f2f3fb;
    border-color: #98a2b3;
  }

  /* Action Buttons */
  .edit-account-button-wrapper {
    justify-content: center;
  }

  .edit-account-button-wrapper :deep(.action-button) {
    width: 100%;
    min-height: 40px;
    height: 40px;
    font-size: var(--fs-small);
    line-height: 20px;
    letter-spacing: 0.014px;
    border-radius: 8px;
    padding: 10px 16px;
    font-weight: 700;
  }

  .edit-actions {
    flex-direction: row;
    width: 100%;
    gap: 8px;
  }

  .edit-actions :deep(.action-button) {
    flex: 1;
    min-height: 40px;
    height: 40px;
    font-size: var(--fs-small);
    line-height: 20px;
    letter-spacing: 0.014px;
    border-radius: 8px;
    padding: 10px 16px;
    font-weight: 700;
  }
}
</style>
